import { Description, PersonOutline, ShoppingCart } from "@mui/icons-material";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { dark_red } from "../../../utils/colors";
import { getQuestions } from "../../../utils/mutations/form";
import type { Question, User } from '../../../utils/types';
import UserForm from '../../widgets/UserForm';
import Footer from '../menuBars/Footer';
import MenuBar from '../menuBars/MenuBar';
import ProfileTab from './ProfileTab';
import PurchasesTab from './PurchasesTab';

interface SettingsPageProps {
  user: User; 
  confType: 'main' | 'hyls';
  isSmallScreen: boolean;
}

export default function SettingsPage({ user, confType, isSmallScreen }: SettingsPageProps) {
  const [currentTab, setCurrentTab] = useState<number>(
    JSON.parse(sessionStorage.getItem('tab') || '0')
  );
  const [questions, setQuestions] = useState<Question[]>([]);

  useEffect(() => {
    sessionStorage.setItem('tab', JSON.stringify(currentTab))
  }, [currentTab]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if(!user.conferenceCode) return;
      const data: Question[] = await getQuestions(user.conferenceCode);
      setQuestions(data);
    };
    fetchQuestions();
  }, [user.conferenceCode]);

  // If we're on the form tab but there are no questions, switch to profile tab
  useEffect(() => {
    if (currentTab === 1 && (!questions || questions.length === 0)) {
      setCurrentTab(0);
    }
  }, [questions, currentTab]);

  const handleChangeTab = (_: React.ChangeEvent<{}>, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Define tabs based on whether there are questions
  const getTabs = () => {
    const tabs = [
      { label: "Profile", icon: <PersonOutline />, value: 0 },
      { label: "Purchases", icon: <ShoppingCart />, value: 2 }
    ];
    
    // Insert Form tab if there are questions
    if (questions && questions.length > 0) {
      tabs.splice(1, 0, { label: "Form", icon: <Description />, value: 1 });
    }
    
    return tabs;
  };

  return (
    <>
      <MenuBar user={user} confType={confType} isSmallScreen={isSmallScreen} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: "64px",
          minHeight: 'calc(100vh - 64px - 130px)',
          padding: "20px 0px",
        }}
      >
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            color: dark_red,
            fontWeight: 'bold',
          }}
        >
          Settings
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isSmallScreen ? 'column' : 'row',
            padding: "20px",
          }}
        >
          {isSmallScreen ? (
            <Tabs
              variant="scrollable"
              value={currentTab}
              onChange={handleChangeTab}
              sx={{
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'center',
                },
                marginBottom: '20px',
              }}
            >
              {getTabs().map((tab) => (
                <Tab key={tab.value} label={tab.label} icon={tab.icon} value={tab.value} />
              ))}
            </Tabs>
          ) : (
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={currentTab}
              onChange={handleChangeTab}
            >
              {getTabs().map((tab) => (
                <Tab key={tab.value} label={tab.label} icon={tab.icon} value={tab.value} />
              ))}
            </Tabs>
          )}  
          <Box
            sx={{
              padding: "0px 20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: '50%',
            }}
          >
            {currentTab === 0 && (
              <ProfileTab isSmallScreen={isSmallScreen} />
            )}
            {currentTab === 1 && questions && questions.length > 0 && (
              user && user.conferenceCode && <UserForm confCode={user.conferenceCode} questions={questions} user={user}/>
            )}
            {currentTab === 2 && (
              user && <PurchasesTab user={user}/>
            )}
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
}
